import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle, Icon } from "@material-ui/core";
import { ThemeProvider as MuiThemeProvider } from "@material-ui/core/styles";
import React, { Component, Fragment } from "react";
import styled from "styled-components";

import { ActionPermissionsObject, PermissionActionType, WorkspaceUserRole } from "common/interfaces";
import { getStaticUrl } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { trackActivity } from "js/core/utilities/utilities";
import { app } from "js/namespaces";
import { UserProfile } from "js/react/components/Avatar";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import BeautifulDialog from "js/react/components/Dialogs/BeautifulDialog";
import { Gap20, Gap30 } from "js/react/components/Gap";
import { dialogTheme } from "js/react/materialThemeOverrides";
import { ProCheckoutDialog, TeamCheckoutDialog } from "js/react/views/Billing/Checkout";

export const UpgradePlanDialogType = {
    UPGRADE_PLAN: "upgrade_plan",
    UPGRADE_PLAN_TO_TEAM: "upgrade_plan_to_team",
    REMOVE_BRANDING: "remove_branding",
    TEAM_NOOP: "team-noop"
};

const UpgradeWorkspaceButton = styled.span`
    cursor: pointer;
    color: #11a9e2;
    font-weight: 600;
    font-size: 16px;
`;

const StyledDialogContent = styled(DialogContent)`
    &&& {
        padding: 8px 34px;
    }
`;

function Subheader({ showTrial }) {
    let subheader;
    if (showTrial) {
        subheader = "Get started with a 14-day free trial of Beautiful.ai Pro to try our advanced features.";
    } else {
        subheader =
            "Fully loaded with advanced features so you'll have all the pro tools you need - to create not" +
            " only a beautiful presentation, but an impact. Every single time.";
    }

    return (
        <div>
            <div className="header-text">{subheader}</div>
            <Gap30 />
            {
                !app.isConstrained &&
                <Fragment>
                    <div className="feature-label">Just Some of the Features You'll Love in Pro:</div>
                    <Gap20 />
                </Fragment>
            }
        </div>
    );
}

function UpgradeToProContents() {
    return (
        <div>
            {
                !app.isConstrained &&
                <div className="feature-scroll">
                    <div className="feature-list">
                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Unlimited.svg")} />
                            <div className="frame">
                                <div className="title">Smart Slides</div>
                                <div className="description">
                                    Get unlimited access to all our Smart Slides with no watermarks.
                                </div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Player.svg")} />
                            <div className="frame">
                                <div className="title">Remove Beautiful.ai Branding</div>
                                <div className="description">
                                    Remove Beautiful.ai branding and set custom playback options for your presentations.
                                </div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Export.svg")} />
                            <div className="frame">
                                <div className="title">Editable PowerPoint Export</div>
                                <div className="description">Export your presentation to a fully editable PowerPoint.</div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Branding.svg")} />
                            <div className="frame">
                                <div className="title">Custom Fonts</div>
                                <div className="description">Import your own fonts to match your company brand.</div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Security.svg")} />
                            <div className="frame">
                                <div className="title">Secured Sharing</div>
                                <div className="description">
                                    Securely share your presentation so that only the people you want are able to view it.
                                </div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Analytics.svg")} />
                            <div className="frame">
                                <div className="title">Analytics</div>
                                <div className="description">
                                    Track your presentations and see who is viewing them and how effective they are.
                                </div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/History.svg")} />
                            <div className="frame">
                                <div className="title">Revision History</div>
                                <div className="description">
                                    Keep an unlimited history of all your slide changes so you can revert back to an older
                                    version at any time.
                                </div>
                            </div>
                        </div>

                        <div className="feature">
                            <img src={getStaticUrl("/images/upgradePlan/Desktop.svg")} />
                            <div className="frame">
                                <div className="title">Desktop Player</div>
                                <div className="description">
                                    Play your presentations online or offline in our desktop application with improved
                                    support for multiple monitors and presenter notes.
                                </div>
                            </div>
                        </div>
                    </div>
                    <Gap20 />
                </div>
            }
            {
                app.isConstrained &&
                <div className="feature-scroll">
                    <div className="feature-list-header">Everything in Basic, plus:</div>
                    <div className="feature-list">
                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Unlimited Slides</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Remove Beautiful.ai Branding</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Editable PowerPoint Export</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Custom Fonts</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Secured Sharing</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Analytics</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Revision History</div>
                            </div>
                        </div>

                        <div className="feature">
                            <Icon className="text-blue">check</Icon>
                            <div className="frame">
                                <div className="title">Desktop Player</div>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
}

function handleUpgradeButton(props) {
    const { closeDialog, analytics } = props;

    trackActivity("Upgrade", "Confirm", null, null, analytics, { audit: true });

    const dialogProps = {
        onClose: successfulPurchase => {
            closeDialog(successfulPurchase);
        }
    };

    ShowDialog(ProCheckoutDialog, dialogProps);
}

const StyledUpdgradeSpan = styled.span`
    color: #23aae0;
`;

export class PersonalUpgradePlanDialog extends Component {
    render() {
        const { closeDialog } = this.props;

        const showTrial = !app.user.hasTakenTrial2;
        const cta = showTrial ? "Try Free For 14 Days" : "Upgrade Plan";

        return (
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog maxWidth="md" fullWidth={false} closeDialog={closeDialog}>
                    <div className="upgrade-plan-dialog">
                        <div className="outer">
                            <div className="inner">
                                <div className="header-intro">
                                    Upgrade to <StyledUpdgradeSpan>Beautiful.ai Pro</StyledUpdgradeSpan> for as low as{" "}
                                    <StyledUpdgradeSpan>$12</StyledUpdgradeSpan>/month.
                                </div>

                                <Subheader showTrial={showTrial} />

                                {
                                    !app.isConstrained &&
                                    <Fragment>
                                        <UpgradeToProContents />

                                        <div className="cta-block">
                                            <div className="cta-button" onClick={() => handleUpgradeButton(this.props)}>
                                                {cta}
                                            </div>
                                            <div className="cta-description">
                                                Learn more about our different plans{" "}
                                                <a
                                                    href={getPricingPageUrl(app && app.user.hasTakenTrial2, app && app.user.has("hasTakenTeamTrial2"), "basic")}
                                                    rel="noreferer, ,noopener"
                                                    target="_blank"
                                                >
                                                    here
                                                </a>
                                                .
                                            </div>
                                        </div>
                                    </Fragment>
                                }
                                {
                                    app.isConstrained &&
                                    <Fragment>
                                        <div className="cta-block">
                                            <div className="cta-button" onClick={() => handleUpgradeButton(this.props)}>
                                                {cta}
                                            </div>
                                            <Gap20 />
                                        </div>

                                        <UpgradeToProContents />

                                        <div className="cta-block">
                                            <div className="cta-description">
                                                Learn more about our different plans{" "}
                                                <a
                                                    href={getPricingPageUrl(app && app.user.hasTakenTrial2, app && app.user.has("hasTakenTeamTrial2"), "basic")}
                                                    rel="noreferer, ,noopener"
                                                    target="_blank"
                                                >
                                                    here
                                                </a>
                                                .
                                            </div>
                                        </div>
                                    </Fragment>
                                }

                            </div>
                        </div>
                    </div>
                </BeautifulDialog>
            </MuiThemeProvider>
        );
    }
}

export const TeamUpgradePlanDialog = WorkspaceController.withInitializedState(React.forwardRef(
    function TeamUpgradePlanDialog(props, ref) {
        const { closeDialog, users } = props;

        const canManageBilling = WorkspaceController.actionPermissions[ActionPermissionsObject.BILLING][PermissionActionType.MANAGE];

        const handleUpgradeWorkspace = async () => {
            ShowDialog(TeamCheckoutDialog, { workspaceId: WorkspaceController.workspaceId });
            closeDialog(false);
        };

        if (!canManageBilling) {
            const owners = Object.values(users).filter(user => user.role === WorkspaceUserRole.OWNER);

            return (
                <MuiThemeProvider theme={dialogTheme} ref={ref}>
                    <BeautifulDialog fullWidth={false} closeDialog={() => closeDialog(false)}>
                        <DialogTitle>Ask your Workspace Admin to upgrade your account.</DialogTitle>
                        <StyledDialogContent>
                            <DialogContentText>
                                <span>This feature is only available to Pro Team Members. To get access to this - along with many more features you'll love - ask your Workspace Admin to upgrade your account.</span>
                            </DialogContentText>
                            <h4>These Admins can update your account:</h4>
                            <div className="admins">{owners.slice(0, 2).map(user => <UserProfile key={user.uid} profile={user} />)}</div>
                        </StyledDialogContent>
                        <DialogActions>
                            <Button onClick={() => closeDialog(false)} color="primary">Got It</Button>
                        </DialogActions>
                    </BeautifulDialog>
                </MuiThemeProvider>
            );
        }

        return (
            <MuiThemeProvider theme={dialogTheme} ref={ref}>
                <BeautifulDialog fullWidth={false} closeDialog={() => closeDialog(false)}>
                    <DialogTitle>Upgrade your workspace.</DialogTitle>
                    <StyledDialogContent>
                        <DialogContentText>
                            This feature is only available on the Team plan. To get access to this - along with many more features you'll love -&nbsp;
                            <UpgradeWorkspaceButton onClick={handleUpgradeWorkspace}>
                                upgrade your workspace.
                            </UpgradeWorkspaceButton>
                        </DialogContentText>
                    </StyledDialogContent>
                    <DialogActions>
                        <Button onClick={() => closeDialog(false)} color="primary">Close</Button>
                    </DialogActions>
                </BeautifulDialog>
            </MuiThemeProvider>
        );
    }
));

export class PersonalRemoveBrandingDialog extends Component {
    render() {
        const { closeDialog } = this.props;
        return (
            <MuiThemeProvider theme={dialogTheme}>
                <BeautifulDialog maxWidth="md" fullWidth={false} closeDialog={closeDialog}>
                    <div className="upgrade-plan-dialog">
                        <div className="warning">Want to remove the Beautiful.ai Watermark?</div>
                        <div className="outer">
                            <div className="inner">
                                <div className="header-intro">
                                    Upgrade to <StyledUpdgradeSpan >Beautiful.ai Pro</StyledUpdgradeSpan> for as low as{" "}
                                    <StyledUpdgradeSpan >$12</StyledUpdgradeSpan>/month.
                                </div>

                                <Subheader />

                                <UpgradeToProContents />

                                <div className="cta-block">
                                    <div className="cta-button" onClick={() => handleUpgradeButton(this.props)}>
                                        Upgrade to Beautiful.ai Pro
                                    </div>
                                    <div className="cta-description">
                                        Learn more about our different plans{" "}
                                        <a
                                            href={getPricingPageUrl(app && app.user.hasTakenTrial2, app && app.user.has("hasTakenTeamTrial2"), "basic")}
                                            rel="noreferer, ,noopener"
                                            target="_blank"
                                        >
                                            here
                                        </a>
                                        .
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </BeautifulDialog>
            </MuiThemeProvider>
        );
    }
}
